// ⭐ NO CHANGES ABOVE imports
import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_Row,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../Utils/axiosInstance";
import { useQuery } from "@tanstack/react-query";
import { Col, Container, Row } from "reactstrap";
import Accordion from "react-bootstrap/Accordion";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
type ApiBrokerSetting = {
  id: string;
  API_broker_name: string;
  broker: string;
  broker_name?: string;
};

export type BrokerOption = {
  id: string;
  name: string;
};

const ApiBrokerSettings = () => {
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");

  // ⭐ Remove editingRow completely (no row editing)
  const [editId, setEditId] = useState<string | null>(null); // ⭐ which row is in edit mode

  // ⭐ Form handles BOTH create + edit
  const [formData, setFormData] = useState({
    API_broker_name: "",
    broker: "",
  });

  const [errors, setErrors] = useState({
    API_broker_name: false,
    broker: false,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  // Broker options
  const { data: brokerOptions = [] } = useQuery<BrokerOption[]>({
    queryKey: ["api-broker-list"],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/paper-app/api-broker-settings-brokers/`
      );
      return res.data;
    },
    refetchOnWindowFocus: false,
  });

  // Table data
  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery<ApiBrokerSetting[]>({
    queryKey: ["api-broker-settings"],
    queryFn: async () => {
      const res = await axiosInstance.get(`/paper-app/api-broker-settings/`);
      return res.data;
    },
    refetchOnWindowFocus: false,
  });

  // Styling
  const compactInputStyle = {
    width: "250px",
    "& .MuiInputBase-root": {
      height: "32px",
      fontSize: "0.75rem",
      fontWeight: "bold",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.9rem",
      transform: "translate(14px, -8px) scale(0.74)", // always in top position
      fontWeight: "bold",
      color: "black",
      paddingRight: "0.4rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#b9baeb85",
      borderWidth: "2px",
    },
    "& .MuiMenuItem-root": {
      fontSize: "0.75rem",
    },
  };

  const validate = () => {
    const newErrors = {
      API_broker_name: !formData.API_broker_name.trim(),
      broker: !formData.broker,
    };
    setErrors(newErrors);
    return !newErrors.API_broker_name && !newErrors.broker;
  };

  // ----------------------------
  // ⭐ COLUMNS (unchanged)
  // ----------------------------
  const columns = useMemo(
    () => [
      {
        accessorKey: "API_broker_name",
        header: "API Broker Name",
        size: 100,
        minSize: 100,
        maxSize: 600,
      },
      {
        accessorKey: "broker",
        header: "Broker",
        Cell: ({ row }: any) => row.original.broker_name,
        size: 100,
        minSize: 100,
        maxSize: 600,
      },
    ],
    []
  );

  // ----------------------------
  // ⭐ CREATE / UPDATE FORM
  // ----------------------------
  const handleCreate = async () => {
    if(!validate()){
      toast.error("Please fill in all required fields!"); 
      return;
    }
    try {
      await axiosInstance.post(`/paper-app/api-broker-settings/`, formData);
      setFormData({ API_broker_name: "", broker: "" });
      refetch();
      toast.success("API Broker added successfully!"); // ⭐ Toast
    } catch (err) {
      toast.error("Failed to add API Broker!"); // ⭐ Toast
      console.error(err);
    }
  };

  const handleClear = () => {
    setFormData({
      API_broker_name: "",
      broker: "",
    });
    setErrors({
      API_broker_name: false,
      broker: false,
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormData({
      API_broker_name: "",
      broker: "",
    });
  };

  const handleUpdate = async () => {
    if (!editId) return;

    try {
      await axiosInstance.patch(
        `/paper-app/api-broker-settings/${editId}/`,
        formData
      );

      toast.success("API Broker updated successfully!"); // ⭐ Toast

      setEditId(null);
      setFormData({ API_broker_name: "", broker: "" });
      setErrors({
        API_broker_name: false,
        broker: false,
      });
      refetch();
    } catch (err) {
      toast.error("Failed to update API Broker!"); // ⭐ Toast
      console.error(err);
    }
  };

  // ⭐ When clicking table Edit button
  const fillFormForEdit = (row: any) => {
    setEditId(row.original.id);
    setFormData({
      API_broker_name: row.original.API_broker_name,
      broker: row.original.broker,
    });
    setErrors({
      API_broker_name: false,
      broker: false,
    });
  };

  const handleDelete = (row: any) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this API Broker Settings?",
      content: "Deleting...",
      onOk: async () => {
        try {
          await axiosInstance.delete(
            `/paper-app/api-broker-settings/${row.original.id}/`
          );
          toast.success("Deleted successfully!");
          refetch();
        } catch (err) {
          toast.error("Failed to delete!");
          console.error(err);
        }
      },
    });
  };

  const handleBulkDelete = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;

    showDeleteConfirmModal({
      title: `Are you sure you want to delete ${selectedIds.length} selected record(s)?`,
      content: "Deleting...",
      onOk: async () => {
        try {
          await Promise.all(
            selectedIds.map((id) =>
              axiosInstance.delete(`/paper-app/api-broker-settings/${id}/`)
            )
          );
          toast.success(`${selectedIds.length} record(s) deleted successfully!`);
          setRowSelection({});
          refetch();
        } catch (err) {
          toast.error("Failed to delete selected records!");
          console.error(err);
        }
      },
    });
  };

  const handleCopyRow = (row: any) => {
    setEditId(null);
    setFormData({
      API_broker_name: row.original.API_broker_name,
      broker: row.original.broker,
    });
    setErrors({ API_broker_name: false, broker: false });
  };

  // ----------------------------
  // TABLE SETTINGS (NO ROW EDIT)
  // ----------------------------
  const table: any = useMaterialReactTable({
    columns,
    data,
    manualPagination: false,
    manualSorting: false,
    manualFiltering: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    layoutMode: "grid",
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    muiTopToolbarProps: ({ table }) => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableProps: {
      sx: { tableLayout: "auto", width: "100%" },
    },
    muiTableContainerProps: ({ table }) => ({
      sx: { backgroundColor: "#fff" },
      className: "table-container-wrapper__new",
    }),

    muiTableFooterProps: ({ table }) => ({
      sx: { backgroundColor: "#fff" },
      className: "table-footer-wrapper__new",
    }),

    muiBottomToolbarProps: ({ table }) => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "bottom-toolbar-wrapper__new",
    }),

    getRowId: (row: any) => row?.id,


    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 120,
        minSize: 60,
        maxSize: 120,
        grow: false,
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {Object.keys(rowSelection).length > 0 && (
          <Button
            onClick={handleBulkDelete}
            style={{ color: "red", fontSize: "small", textTransform: "none" }}
            startIcon={<DeleteIcon style={{ fontSize: "small" }} />}
          >
            Delete Selected ({Object.keys(rowSelection).length})
          </Button>
        )}
      </Box>
    ),
    // ⭐ Edit button now fills form
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "0.5rem" }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => fillFormForEdit(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => handleDelete(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    API Broker Settings
                  </Accordion.Header>

                  <Accordion.Body>
                    <div className="form_contain">
                      <div
                        className={`flex formWrapper ${inputBackgroundColour !== "" ? "inputboxcustom" : ""
                          }`}
                        style={{ backgroundColor: inputBackgroundColour }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            p: 1,
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            className="form_default"
                            sx={{
                              display: "flex",
                              flexWrap: "nowrap",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              gap: 1,
                            }}
                          >
                            {/* API Broker Name */}
                            <TextField
                              size="small"
                              label="API Broker Name"
                              value={formData.API_broker_name}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  API_broker_name: e.target.value,
                                })
                              }
                              sx={compactInputStyle}
                            />

                            {/* Broker */}
                            <TextField
                              size="small"
                              select
                              label="Broker"
                              value={formData.broker}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  broker: e.target.value,
                                })
                              }
                              sx={compactInputStyle}
                            >
                              {brokerOptions.map((opt: any) => (
                                <MenuItem
                                  key={opt.id}
                                  value={opt.id}
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {opt.name}
                                </MenuItem>
                              ))}
                            </TextField>

                            {/* ⭐ BUTTON changes Add ↔ Update */}
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {/* Add / Update */}
                              <Button
                                onClick={editId ? handleUpdate : handleCreate}
                                variant="contained"
                                sx={{
                                  height: "32px",
                                  minWidth: "100px",
                                  backgroundColor: "#00bcd4",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {editId ? "Update" : "Add"}
                              </Button>

                              {/* Clear button */}
                              <Button
                                onClick={handleClear}
                                variant="outlined"
                                sx={{
                                  height: "32px",
                                  minWidth: "80px",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Clear
                              </Button>

                              {/* Cancel button (ONLY in edit mode) */}
                              {editId && (
                                <Button
                                  onClick={handleCancelEdit}
                                  variant="outlined"
                                  color="error"
                                  sx={{
                                    height: "32px",
                                    minWidth: "80px",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="table-ad__wrapper">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                </div>
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default ApiBrokerSettings;
